import { useEffect, useState } from "react";
import AccountButton from "../AccountButton";
import AccountSection from "../AccountSection";
import AccountTextarea from "../AccountTextarea";
import {
  addAllergy,
  deleteAllergy,
  getAllergies,
  updateAllergy,
} from "../api/apiFunctions";
import { DisplayOnCardPill } from "../components/Pills";
import { t } from "../i18n";
import { getAuthToken } from "../utils/auth";
import { replaceNullWithPlaceholder } from "../utils";

export default function AllergiesPage({ lang = "en", goToTab }) {
  const [search, setSearch] = useState("");
  const [form, setForm] = useState({
    allergy: "",
    details: "",
  });
  const [allergies, setAllergies] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [editingIndex, setEditingIndex] = useState(null);
  const [editForm, setEditForm] = useState({
    name: "",
    details: "",
    status: "active",
    displayOnCard: false,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [allergyToDelete, setAllergyToDelete] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    loadAllergies();
  }, []);

  const clearErrors = () => {
    setTimeout(() => {
      setError("");
    }, 3000);
  };

  // Load allergies from API
  const loadAllergies = async () => {
    try {
      setIsLoading(true);
      setError("");

      const token = getAuthToken();
      if (!token) {
        throw new Error("Authentication token not found. Please login again.");
      }

      const response = await getAllergies(token);

      const allergiesData = response?.data?.allergies || [];

      // Transform data to match expected format
      const transformedAllergies = allergiesData.map((allergy) => {
        return {
          id: allergy._id || allergy.id,
          name: allergy.name || allergy.allergyName,
          details: allergy.details || allergy.description || "",
          status: allergy.status || "active",
          displayOnCard: allergy.displayOnCard,
        };
      });

      setAllergies(transformedAllergies);
    } catch (error) {
      console.error("Error loading allergies:", error);
      setError(error.message || "Failed to load allergies. Please try again.");
    } finally {
      setIsLoading(false);
      clearErrors();
    }
  };

  const statusOptions = [
    { value: "all", label: t("allStatuses", lang) },
    { value: "active", label: t("active", lang) },
    { value: "inactive", label: t("inactive", lang) },
  ];

  // Handlers
  const handleSearchChange = (e) => setSearch(e.target.value);
  const handleStatusChange = (e) => setStatusFilter(e.target.value);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const saveAllergy = async () => {
    if (!form.allergy.trim()) {
      setError("Please enter an allergy name");
      clearErrors();
      return false;
    }

    try {
      setIsSaving(true);
      setError("");
      setSuccessMessage("");

      const token = getAuthToken();

      if (!token) {
        throw new Error("Authentication token not found. Please login again.");
      }

      const allergyData = {
        name: form.allergy.trim(),
        details: form.details.trim(),
        status: "active",
      };

      const response = await addAllergy(allergyData, token);

      if (response.success) {
        // Add the new allergy to the local state
        const newAllergy = {
          id: response.data.allergyId || "",
          name: form.allergy.trim(),
          details: form.details.trim(),
          status: "active",
        };
        setAllergies((prev) => [...prev, newAllergy]);

        // Clear the form
        setForm({ allergy: "", details: "" });
        setSuccessMessage("Allergy added successfully!");

        // Clear success message after 3 seconds
        setTimeout(() => setSuccessMessage(""), 3000);
        return true; // Return true to indicate success
      } else {
        throw new Error(response.message || "Failed to add allergy");
      }
    } catch (error) {
      console.error("Error adding allergy:", error);
      setError(error.message || "Failed to add allergy. Please try again.");
      return false; // Return false to indicate failure
    } finally {
      setIsSaving(false);
      clearErrors();
    }
  };

  const removeAllergy = (index) => {
    const allergy = allergies[index];
    setAllergyToDelete({ index, allergy });
    setShowDeleteConfirm(true);
  };

  const confirmDeleteAllergy = async () => {
    if (!allergyToDelete) return;

    try {
      setIsSaving(true);
      setError("");
      setSuccessMessage("");

      const token = getAuthToken();
      if (!token) {
        throw new Error("Authentication token not found. Please login again.");
      }

      const response = await deleteAllergy(allergyToDelete.allergy.id, token);

      if (response.success) {
        // Remove from local state
        setAllergies((prev) =>
          prev.filter((_, i) => i !== allergyToDelete.index),
        );
        setSuccessMessage("Allergy deleted successfully!");

        // Clear success message after 3 seconds
        setTimeout(() => setSuccessMessage(""), 3000);
      } else {
        throw new Error(response.message || "Failed to delete allergy");
      }
    } catch (error) {
      console.error("Error deleting allergy:", error);
      setError(error.message || "Failed to delete allergy. Please try again.");
    } finally {
      setIsSaving(false);
      setShowDeleteConfirm(false);
      setAllergyToDelete(null);
      clearErrors();
    }
  };

  const startEdit = (index) => {
    const allergy = allergies[index];
    setEditingIndex(index);
    setEditForm({
      name: allergy.name,
      details: allergy.details,
      status: allergy.status,
    });
  };

  const cancelEdit = () => {
    setEditingIndex(null);
    setEditForm({ name: "", details: "", status: "active" });
  };

  const saveEdit = async () => {
    if (!editForm.name.trim()) {
      setError("Please enter an allergy name");
      return;
    }

    try {
      setIsSaving(true);
      setError("");
      setSuccessMessage("");

      const token = getAuthToken();
      if (!token) {
        throw new Error("Authentication token not found. Please login again.");
      }

      const allergy = allergies[editingIndex];

      if (!allergy.id) {
        throw new Error("Try refreshing the page.");
      }

      const allergyData = {
        name: editForm.name.trim(),
        details: editForm.details.trim(),
        status: editForm.status,
      };

      const response = await updateAllergy(allergy.id, allergyData, token);

      if (response.success) {
        // Update the local state
        setAllergies((prev) =>
          prev.map((allergy, index) =>
            index === editingIndex
              ? {
                  ...allergy,
                  name: editForm.name.trim(),
                  details: editForm.details.trim(),
                  status: editForm.status,
                }
              : allergy,
          ),
        );
        setSuccessMessage("Allergy updated successfully!");

        // Clear success message after 3 seconds
        setTimeout(() => setSuccessMessage(""), 3000);
        cancelEdit();
      } else {
        throw new Error(response.message || "Failed to update allergy");
      }
    } catch (error) {
      console.error("Error updating allergy:", error);
      setError(error.message || "Failed to update allergy. Please try again.");
    } finally {
      setIsSaving(false);
      clearErrors();
    }
  };

  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    setEditForm((prev) => ({ ...prev, [name]: value }));
  };

  // Filtered allergies
  const filteredAllergies = allergies.filter((allergy) => {
    const matchesSearch = allergy.name
      ?.toLowerCase()
      .includes(search.toLowerCase());
    const matchesStatus =
      statusFilter === "all" || allergy.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const showInfoColumn = filteredAllergies.some(
    (allergy) => allergy.displayOnCard === true,
  );

  return (
    <div className="min-h-screen bg-[#f7f8fa] flex flex-col items-center pb-8">
      <div className="w-full max-w-4xl px-4 md:px-0">
        <div className="bg-white rounded-xl shadow p-8 mt-4">
          {/* Error Message */}
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center">
                <svg
                  className="w-5 h-5 text-red-400 mr-2"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className="text-red-700 text-sm">{error}</span>
              </div>
            </div>
          )}

          {/* Success Message */}
          {successMessage && (
            <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center">
                <svg
                  className="w-5 h-5 text-green-400 mr-2"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className="text-green-700 text-sm">{successMessage}</span>
              </div>
            </div>
          )}

          {/* Known Allergies */}
          <AccountSection>{t("knownAllergies", lang)}</AccountSection>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <label className="text-xs font-semibold text-[#837f90] mb-1 block">
                {t("allergy", lang)}
              </label>

              {/* None option checkbox */}

              <div className="relative flex items-center mb-5">
                <input
                  type="text"
                  name="allergy"
                  value={form.allergy}
                  onChange={handleFormChange}
                  placeholder={t("enterAllergy", lang)}
                  className={`w-full border border-gray-200 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#4962d8]`}
                />
                <span className="absolute right-2 top-1/2 -translate-y-1/2">
                  <svg
                    width="18"
                    height="18"
                    fill="none"
                    stroke="#837f90"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                  >
                    <circle cx="11" cy="11" r="8" />
                    <path d="M21 21l-4.35-4.35" />
                  </svg>
                </span>
              </div>
            </div>
            <div>
              <AccountTextarea
                label={t("detailsNotes", lang)}
                name="details"
                value={form.details}
                onChange={handleFormChange}
                rows={7}
                placeholder={t("addNotesDetails", lang)}
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col md:flex-row gap-4 mb-8">
            <AccountButton
              className="md:w-1/3"
              disabled={isSaving}
              onClick={() => goToTab("prev")}
            >
              <span className="mr-2">&laquo;</span> {t("back", lang)}
            </AccountButton>
            <AccountButton
              className="md:w-1/3"
              onClick={() => saveAllergy()}
              disabled={isSaving}
            >
              {isSaving ? (
                <div className="flex items-center justify-center">
                  <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Saving...
                </div>
              ) : (
                t("save", lang)
              )}
            </AccountButton>
            <AccountButton
              className="md:w-1/3"
              onClick={() => goToTab("next")}
              disabled={isSaving}
            >
              {t("next", lang)} <span className="ml-2">&raquo;</span>
            </AccountButton>
          </div>

          {/* My Allergies Table */}
          <AccountSection>{t("myAllergies", lang)}</AccountSection>
          <div className="flex flex-col md:flex-row md:items-center gap-2 mb-2">
            <div className="flex-1 flex items-center gap-2">
              <input
                id="allergy-search"
                type="text"
                value={search}
                onChange={handleSearchChange}
                placeholder={t("searchPlaceholder", lang)}
                className="border border-gray-200 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-[#4962d8]"
                style={{ maxWidth: 200 }}
              />
              <span className="inline-flex items-center justify-center w-8 h-8 rounded bg-[#f7f8fa] border border-gray-200">
                <svg
                  width="16"
                  height="16"
                  fill="none"
                  stroke="#837f90"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <circle cx="11" cy="11" r="8" />
                  <path d="M21 21l-4.35-4.35" />
                </svg>
              </span>
            </div>
            <select
              value={statusFilter}
              onChange={handleStatusChange}
              className="border-0 border-b-2 border-[#4962d8] focus:border-[#4962d8] focus:outline-none focus:shadow-none text-sm py-1 px-0 bg-transparent text-black font-medium"
              style={{ maxWidth: 140 }}
            >
              {statusOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <div className="overflow-x-auto max-w-xs sm:max-w-full">
            <table className="bg-white text-sm min-w-full">
              <thead>
                <tr className="bg-[#f7f8fa] text-[#837f90]">
                  {showInfoColumn && (
                    <th className="px-4 py-2 text-left font-semibold">Info</th>
                  )}
                  <th className="px-4 py-2 text-left font-semibold">
                    {t("tableName", lang)}
                  </th>
                  <th className="px-4 py-2 text-left font-semibold">
                    {t("tableDetails", lang)}
                  </th>
                  <th className="px-4 py-2 text-left font-semibold">
                    {t("status", lang)}
                  </th>
                  <th className="px-4 py-2 text-left font-semibold">
                    {t("actions", lang)}
                  </th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan={4} className="text-center py-8">
                      <div className="flex items-center justify-center">
                        <svg
                          className="animate-spin -ml-1 mr-3 h-6 w-6 text-[#4962d8]"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        <span className="text-[#837f90]">
                          Loading allergies...
                        </span>
                      </div>
                    </td>
                  </tr>
                ) : filteredAllergies.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="text-center py-8 text-[#837f90]">
                      <div className="flex flex-col items-center">
                        <svg
                          className="w-12 h-12 text-gray-300 mb-3"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="1"
                            d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6-4h6m2 5.291A7.962 7.962 0 0112 15c-2.34 0-4.29-1.009-5.824-2.5M15 6.5a3 3 0 11-6 0 3 3 0 016 0z"
                          />
                        </svg>
                        <p className="text-lg font-medium mb-2">
                          No allergies added yet
                        </p>
                        <p className="text-sm text-gray-500">
                          Add your first allergy using the form above
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredAllergies.map((allergy, idx) => {
                    const actualIndex = allergies.findIndex(
                      (a) => a === allergy,
                    );
                    const isEditing = editingIndex === actualIndex;

                    return (
                      <tr key={idx} className="border-t border-gray-100">
                        {isEditing ? (
                          <>
                            {showInfoColumn && (
                              <td className="px-4 py-2">
                                {allergy.displayOnCard && <DisplayOnCardPill />}
                              </td>
                            )}
                            <td className="px-4 py-2">
                              <input
                                type="text"
                                name="name"
                                value={editForm.name}
                                onChange={handleEditFormChange}
                                className="w-full border border-gray-200 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-[#4962d8]"
                              />
                            </td>
                            <td className="px-4 py-2">
                              <textarea
                                name="details"
                                value={editForm.details}
                                onChange={handleEditFormChange}
                                rows={2}
                                className="w-full border border-gray-200 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-[#4962d8] resize-none"
                              />
                            </td>
                            <td className="px-4 py-2">
                              <select
                                name="status"
                                value={editForm.status}
                                onChange={handleEditFormChange}
                                className="w-full border border-gray-200 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-[#4962d8]"
                              >
                                <option value="active">
                                  {t("active", lang)}
                                </option>
                                <option value="inactive">
                                  {t("inactive", lang)}
                                </option>
                              </select>
                            </td>
                            <td className="px-4 py-2">
                              <div className="flex items-center gap-2">
                                <button
                                  type="button"
                                  onClick={saveEdit}
                                  className="text-green-500 hover:text-green-700 p-1 rounded hover:bg-green-50"
                                  title={t("save", lang)}
                                >
                                  <svg
                                    width="16"
                                    height="16"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    viewBox="0 0 24 24"
                                  >
                                    <polyline points="9,11 12,14 22,4" />
                                    <path d="M21,12v7a2,2,0,0,1-2,2H5a2,2,0,0,1-2-2V5a2,2,0,0,1,2-2h11" />
                                  </svg>
                                </button>
                                <button
                                  type="button"
                                  onClick={cancelEdit}
                                  className="text-gray-500 hover:text-gray-700 p-1 rounded hover:bg-gray-50"
                                  title={t("cancel", lang)}
                                >
                                  <svg
                                    width="16"
                                    height="16"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    viewBox="0 0 24 24"
                                  >
                                    <line x1="18" y1="6" x2="6" y2="18" />
                                    <line x1="6" y1="6" x2="18" y2="18" />
                                  </svg>
                                </button>
                              </div>
                            </td>
                          </>
                        ) : (
                          <>
                            {showInfoColumn && (
                              <td className="px-4 py-2">
                                {Boolean(allergy.displayOnCard) && (
                                  <DisplayOnCardPill />
                                )}
                              </td>
                            )}
                            <td className="px-4 py-2">
                              {replaceNullWithPlaceholder(
                                allergy.name,
                                "No Name",
                              )}
                            </td>
                            <td className="px-4 py-2">
                              {replaceNullWithPlaceholder(
                                allergy.details,
                                "No Details",
                              )}
                            </td>
                            <td className="px-4 py-2">
                              <span
                                className={`inline-block px-3 py-1 text-xs rounded-full ${
                                  allergy.status === "active"
                                    ? "bg-green-100 text-green-700"
                                    : "bg-red-100 text-red-700"
                                }`}
                              >
                                {t(allergy.status, lang)}
                              </span>
                            </td>
                            <td className="px-4 py-2">
                              <div className="flex items-center gap-2">
                                <button
                                  type="button"
                                  onClick={() => startEdit(actualIndex)}
                                  className="text-blue-500 hover:text-blue-700 p-1 rounded hover:bg-blue-50"
                                  title={t("edit", lang)}
                                >
                                  <svg
                                    width="16"
                                    height="16"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    viewBox="0 0 24 24"
                                  >
                                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                  </svg>
                                </button>
                                <button
                                  type="button"
                                  onClick={() => removeAllergy(actualIndex)}
                                  className="text-red-500 hover:text-red-700 p-1 rounded hover:bg-red-50"
                                  title={t("delete", lang)}
                                >
                                  <svg
                                    width="16"
                                    height="16"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    viewBox="0 0 24 24"
                                  >
                                    <polyline points="3,6 5,6 21,6" />
                                    <path d="M19,6v14a2,2,0,0,1-2,2H7a2,2,0,0,1-2-2V6m3,0V4a2,2,0,0,1,2-2h4a2,2,0,0,1,2,2V6" />
                                    <line x1="10" y1="11" x2="10" y2="17" />
                                    <line x1="14" y1="11" x2="14" y2="17" />
                                  </svg>
                                </button>
                              </div>
                            </td>
                          </>
                        )}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0 w-10 h-10 mx-auto bg-red-100 rounded-full flex items-center justify-center">
                <svg
                  className="w-6 h-6 text-red-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                  />
                </svg>
              </div>
            </div>
            <div className="text-center">
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Delete Allergy
              </h3>
              <p className="text-sm text-gray-500 mb-6">
                Are you sure you want to delete "
                {allergyToDelete?.allergy?.name}"? This action cannot be undone.
              </p>
              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setAllergyToDelete(null);
                  }}
                  className="flex-1 bg-gray-100 text-gray-700 px-4 py-2 rounded-md text-sm font-medium hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={confirmDeleteAllergy}
                  disabled={isSaving}
                  className="flex-1 bg-red-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isSaving ? (
                    <div className="flex items-center justify-center">
                      <svg
                        className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Deleting...
                    </div>
                  ) : (
                    "Delete"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
